import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTooltip } from '@angular/material/tooltip';
import { ProjectNavComponent } from '../../projects/project-nav/project-nav.component';
import { PageHeaderComponent } from '../../components/page-header/page-header.component';
import { SearchBarComponent } from '../../components/search-bar/search-bar.component';
import { StatGroupComponent } from '../../components/stat-group/stat-group.component';
import { IconComponent } from '../../components/icon/icon.component';
import { TagFilterComponent, TagFilterOption } from '../../components/tag-filter/tag-filter.component';
import { countTagOptions, tagMatch } from '../../components/tag-filter/tag-filter.utils';
import { SavedViewOption, SavedViewsMenuComponent } from '../../shared/saved-views/saved-views-menu.component';
import { ObjectMappingService } from '../../services/object-mapping.service';
import { ProjectRoleService } from '../../services/project-role.service';
import { SavedViewService } from '../../services/saved-view.service';
import { StatusKind, StatusPillComponent } from '../../components/status-pill/status-pill.component';
import { ObjectMappingCreateDialogComponent } from './object-mapping-create-dialog/object-mapping-create-dialog.component';
import { ObjectMappingSummary } from '../../models/object-mapping.model';
import { SavedView, SavedViewContext } from '../../models/saved-view.model';
import {
  buildObjectMappingSavedViewFiltersJson,
  parseObjectMappingSavedViewFilters,
} from './object-mapping-saved-views.utils';
import { technologyKey } from './technology';

const SAVED_VIEW_CONTEXT: SavedViewContext = 'OBJECT_MAPPINGS';

interface LibraryState {
  /** `null`: die Pille „leer“, die keine Art von `app-status-pill` ist. */
  kind: StatusKind | null;
  key: string;
  count: number;
}

/** Eine Pille je Testelement: leer, brüchig vor nie getroffen; unbenutzt und verschwunden zaehlen nicht. */
export function libraryState(library: ObjectMappingSummary): LibraryState {
  if (library.entryCount === 0) return { kind: null, key: 'objectMappings.state.pill.empty', count: 0 };
  const fragile = library.fragileCount ?? 0;
  if (fragile > 0) return { kind: 'pending', key: 'objectMappings.state.pill.fragile', count: fragile };
  const neverHit = library.neverHitCount ?? 0;
  if (neverHit > 0) return { kind: 'inaktiv', key: 'objectMappings.state.pill.neverHit', count: neverHit };
  return { kind: 'aktiv', key: 'objectMappings.state.pill.allHit', count: 0 };
}

/**
 * Seite „Elemente“: die Testelemente eines Projekts (NEX-1276, NEX-2447), Design
 * `page-objektbibliothek.jsx:263-333`. Kennzahlen und Tag-Optionen zaehlen ueber den
 * ungefilterten Bestand; Suche und Tags sind mit UND verknuepft.
 */
@Component({
  selector: 'app-object-mapping-list',
  imports: [
    DatePipe,
    TranslatePipe,
    MatButton,
    MatIcon,
    MatProgressSpinner,
    MatTooltip,
    ProjectNavComponent,
    PageHeaderComponent,
    SearchBarComponent,
    StatGroupComponent,
    IconComponent,
    TagFilterComponent,
    SavedViewsMenuComponent,
    StatusPillComponent,
    ObjectMappingCreateDialogComponent,
  ],
  templateUrl: './object-mapping-list.component.html',
  styleUrl: './object-mapping-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ObjectMappingListComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly service = inject(ObjectMappingService);
  private readonly savedViewService = inject(SavedViewService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly isReadOnly = inject(ProjectRoleService).isReadOnly;

  readonly projectId: string = this.route.snapshot.params['projectId'];

  readonly loading = signal(true);
  readonly loadError = signal(false);
  readonly searchTerm = signal('');
  readonly selectedTags = signal<string[]>([]);
  readonly libraries = signal<ObjectMappingSummary[]>([]);
  readonly creating = signal(false);
  readonly stateOf = libraryState;
  readonly technologyOf = technologyKey;

  readonly savedViews = signal<SavedView[]>([]);
  readonly activeSavedViewName = signal<string | null>(null);
  readonly savedViewsError = signal<string | null>(null);

  readonly elementCount = computed(() => this.libraries().length);
  readonly objectCount = computed(() => this.libraries().reduce((sum, lib) => sum + lib.entryCount, 0));
  readonly existingNames = computed(() => this.libraries().map((lib) => lib.name));
  readonly tagOptions = computed<TagFilterOption[]>(() => countTagOptions(this.libraries(), this.selectedTags()));

  readonly savedViewsOptions = computed<SavedViewOption[]>(() =>
    this.savedViews().map((view) => ({ name: view.name, summary: this.summarize(view) })),
  );

  readonly filtered = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const tags = this.selectedTags();
    return this.libraries().filter(
      (lib) => (!term || lib.name.toLowerCase().includes(term)) && tagMatch(lib.tags, tags),
    );
  });

  ngOnInit(): void {
    this.load();
    this.loadSavedViews();
  }

  load(): void {
    this.loading.set(true);
    this.loadError.set(false);
    this.service
      .listAll(this.projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (all) => {
          this.libraries.set(all);
          this.loading.set(false);
          this.pruneStaleTags();
        },
        error: () => {
          this.libraries.set([]);
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
  }

  onSearchChange(term: string): void {
    this.searchTerm.set(term);
    this.activeSavedViewName.set(null);
  }

  onTagsChange(tags: string[]): void {
    this.selectedTags.set(tags);
    this.activeSavedViewName.set(null);
  }

  onSavedViewSelected(name: string): void {
    const view = this.savedViews().find((each) => each.name === name);
    if (!view) return;
    const filters = parseObjectMappingSavedViewFilters(view.filtersJson);
    this.savedViewsError.set(null);
    this.searchTerm.set(filters.search);
    this.selectedTags.set(filters.tags ?? []);
    // Vor dem Abgleich setzen: verwirft er Tags, nimmt er die Markierung wieder weg.
    this.activeSavedViewName.set(view.name);
    this.pruneStaleTags();
  }

  onSavedViewSaved(name: string): void {
    const filtersJson = buildObjectMappingSavedViewFiltersJson(this.searchTerm(), this.selectedTags());
    this.savedViewService
      .upsert(this.projectId, { name, context: SAVED_VIEW_CONTEXT, filtersJson })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (saved) => {
          this.savedViews.update((list) => sortedByName([...list.filter((each) => each.name !== saved.name), saved]));
          this.activeSavedViewName.set(saved.name);
          this.savedViewsError.set(null);
        },
        error: () => this.savedViewsError.set(this.translate.instant('objectMappings.savedViews.saveError')),
      });
  }

  onSavedViewDeleted(name: string): void {
    this.savedViewsError.set(null);
    this.savedViews.update((list) => list.filter((each) => each.name !== name));
    if (this.activeSavedViewName() === name) this.activeSavedViewName.set(null);
  }

  onSavedViewDeleteFailed(): void {
    this.savedViewsError.set(this.translate.instant('objectMappings.savedViews.deleteError'));
  }

  open(library: ObjectMappingSummary): void {
    this.router.navigate(['/projects', this.projectId, 'object-mappings', library.id]);
  }

  /** Die Liste bleibt offen (AK12); das neue Testelement steht nach Name einsortiert darin. */
  onCreated(library: ObjectMappingSummary): void {
    this.creating.set(false);
    this.libraries.update((list) => sortedByName([...list.filter((each) => each.id !== library.id), library]));
  }

  private loadSavedViews(): void {
    this.savedViewService
      .list(this.projectId, SAVED_VIEW_CONTEXT)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (views) => this.savedViews.set(views),
        error: () => this.savedViews.set([]),
      });
  }

  private summarize(view: SavedView): string {
    const filters = parseObjectMappingSavedViewFilters(view.filtersJson);
    const parts: string[] = [];
    if (filters.search) parts.push(`„${filters.search}“`);
    if (filters.tags?.length) {
      parts.push(this.translate.instant('objectMappings.savedViews.summaryTags', { tags: filters.tags.join(', ') }));
    }
    return parts.length > 0 ? parts.join(' · ') : this.translate.instant('objectMappings.savedViews.summaryAll');
  }

  /** Erst mit geladener Liste: Vorher waere jeder Tag „unbekannt“. */
  private pruneStaleTags(): void {
    if (this.loading() || this.libraries().length === 0) return;
    const available = new Set(this.libraries().flatMap((lib) => lib.tags ?? []));
    const tags = this.selectedTags();
    const kept = tags.filter((tag) => available.has(tag));
    if (kept.length === tags.length) return;
    this.selectedTags.set(kept);
    this.activeSavedViewName.set(null);
  }
}

function sortedByName<T extends { name: string }>(list: T[]): T[] {
  return list.sort((a, b) => a.name.localeCompare(b.name));
}
